import './Page.scss';
import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';
import { Collapse, Nav } from 'react-bootstrap';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import ReactGA from 'react-ga4';
import cn from 'classnames';
import { PageToolbarSlotContext } from './PageToolbarSlot';
import { useAppSelector } from '../../redux';
import { useModalInfo } from '../../redux/modals';
import { isInternalSelector, useUpToDate } from '../../redux/user';
import { NO_ID_SOURCE_NOTE } from '../../utils/error-reference';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { useErrorReport } from './useErrorReport';
import { extractTrailingJson } from './page-utils';
import { AlertDialog } from '../AlertDialog';
import { Button } from '../Button';
import { Appearance, Design, Intent, Size } from '../designConstants';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { Img } from '../Img';
import { InteractiveDiv } from '../InteractiveEl';
import { getVideosForPage } from '../VideoTutorial/videoConfig';
import { VideoTutorial } from '../VideoTutorial/VideoTutorial';
import { VideoInfo } from '../VideoTutorial/videoTutorialUtils';

export const AlertBar = ({ children }: PropsWithChildren) => {
  const { dismiss } = useUpToDate();

  return (
    <div className='new-version-alert-bar'>
      <div>{children}</div>
      <Button
        size={Size.SM}
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        onClick={dismiss}
      >
        Dismiss
      </Button>
    </div>
  );
};

export const NewVersionAlertBar = () => {
  const { appUpToDate } = useUpToDate();

  if (appUpToDate) {
    return null;
  }
  return <AlertBar>There is a new version of the app. Please refresh the page.</AlertBar>;
};

export const formatMessage = (message?: ReactNode): typeof message => {
  if (message && typeof message === 'string') {
    const jsonData = extractTrailingJson(message);
    if (jsonData) {
      return (
        <>
          {jsonData.preamble && <p>{jsonData.preamble}</p>}
          <br />
          {jsonData.entries.map(([key, value], i) => (
            <p key={`entry-${i}`}>
              {key}: {typeof value === 'string' ? `"${value}"` : String(value)}
            </p>
          ))}
        </>
      );
    }
    return message.split('\n').map((line, i) => {
      const parts = line.split(/(`.+?`)/);
      return (
        <p key={`p-${i}`}>
          {parts.map((part, j) =>
            part.startsWith('`') ? <code key={`code-${j}`}>{part.slice(1, -1)}</code> : part
          )}
        </p>
      );
    });
  }
  return message;
};

interface ErrorMoreInfoProps {
  rawMessage: string;
  code?: string;
}

// Collapsed by default: the raw backend text + classified code (DEV-24580) for anyone who wants
// to self-diagnose or hand it to support, without putting raw text in front of every user.
const ErrorMoreInfo = ({ rawMessage, code }: ErrorMoreInfoProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div className='error-dialog-more-info'>
      <InteractiveDiv className='more-info-toggle' onClick={() => setOpen(prev => !prev)}>
        <span>More info</span>
        {open ? <MdExpandLess /> : <MdExpandMore />}
      </InteractiveDiv>
      <Collapse in={open}>
        <div>
          <p className='more-info-detail'>
            {code && <code>{code}</code>}
            {rawMessage}
          </p>
        </div>
      </Collapse>
    </div>
  );
};

export interface PageNav {
  label: string;
  key: string;
}

interface PageProps extends PropsWithChildren {
  // Optional because `onlyModals` mode never renders the title/header chrome that uses these.
  title?: ReactNode;
  pageType?: string;
  // Applied to the outermost page root, so a caller can scope its own SCSS
  // (subHeader content included) under a single top-level selector even
  // though subHeader and children render into separate sibling containers.
  className?: string;
  actions?: ReactNode;
  navs?: PageNav[];
  navRenderer?: (label: string) => ReactNode;
  onNav?: (nav: string) => void;
  selectedNav?: string;
  subHeader?: ReactNode;
  minWidth?: string;
  minHeight?: string;
  onlyModals?: boolean;
  isLoading?: boolean;
}

let webVitalsRegistered = false;

export const Page = ({
  children,
  title,
  pageType = '',
  className,
  actions,
  navs,
  navRenderer = label => label,
  onNav,
  selectedNav,
  subHeader,
  minWidth = '1220px',
  minHeight = '400px',
  onlyModals = false,
  isLoading
}: PageProps) => {
  const [toolbarSlot, setToolbarSlot] = useState<HTMLElement | null>(null);
  const [showHelpVideoTutorial, setShowHelpVideoTutorial] = useState(false);
  const [videoList, setVideoList] = useState<VideoInfo[]>([]);
  const loadStartRef = useRef(0);
  const loadTrackedRef = useRef(false);
  const seenLoadingRef = useRef(false);
  useLayoutEffect(() => {
    loadStartRef.current = performance.now();
  }, []);
  const { error, areYouSure } = useModalInfo();
  // Preserve the last non-null modal content so the dialog's exit animation
  // doesn't render with default fallback text after the modal is cleared.
  const [displayedError, setDisplayedError] = useState(error);
  const [displayedAreYouSure, setDisplayedAreYouSure] = useState(areYouSure);
  useEffect(() => {
    if (error) {
      setDisplayedError(error);
    }
  }, [error]);
  useEffect(() => {
    if (areYouSure) {
      setDisplayedAreYouSure(areYouSure);
    }
  }, [areYouSure]);
  const [initialNav] = useState(selectedNav || (navs && navs[0] ? navs[0].key : undefined));
  const [mixpanelTracked, setMixpanelTracked] = useState(false);

  const ourOnNav = useCallback(
    (eventKey: string | null) => {
      if (onNav && eventKey !== null) {
        Mixpanel.register({ active_page: eventKey });
        Mixpanel.track_pageview({ page: eventKey });
        onNav(eventKey);
      }
    },
    [onNav]
  );
  const { cid } = useCompanyInfo();
  const isInternal = useAppSelector(isInternalSelector);

  // Used for GA4 tracking of page types.  Simpler than the <Page /> title, since that can
  // sometimes be a JSX element instead of just a string.
  useEffect(() => {
    if (pageType) {
      document.title = pageType || 'None';

      ReactGA.initialize('G-LCZYGZVKT2', {
        gtagOptions: {
          role: isInternal ? 'internal' : 'external',
          company: cid || 'None'
        }
      });
      ReactGA.send('pageview');
    }
  }, [pageType, isInternal, cid]);

  useEffect(() => {
    const list = getVideosForPage(pageType);
    if (list.length && isInternal) {
      setShowHelpVideoTutorial(true);

      setVideoList(list);
    }
  }, [pageType, isInternal]);

  useEffect(() => {
    if (!pageType || webVitalsRegistered) return;
    webVitalsRegistered = true;
    const report = ({ name, value, rating }: { name: string; value: number; rating: string }) => {
      Mixpanel.track(MxE.WebVital, {
        metric_name: name,
        metric_value: value,
        rating,
        page: pageType
      });
    };
    onCLS(report);
    onFCP(report);
    onINP(report);
    onLCP(report);
    onTTFB(report);
  }, [pageType]);

  useEffect(() => {
    if (isLoading === true) {
      seenLoadingRef.current = true;
    }
    if (isLoading === false && seenLoadingRef.current && pageType && !loadTrackedRef.current) {
      loadTrackedRef.current = true;
      Mixpanel.track(MxE.PageDataLoad, {
        metric_value: Math.round(performance.now() - loadStartRef.current),
        page: pageType
      });
    }
  }, [isLoading, pageType]);

  useEffect(() => {
    if (!mixpanelTracked) {
      if (initialNav && pageType) {
        Mixpanel.register({ page_group: pageType, active_page: initialNav });
        Mixpanel.track_pageview({ page: initialNav });

        setMixpanelTracked(true);
      } else if (pageType) {
        Mixpanel.register({ page_group: pageType, active_page: pageType });
        Mixpanel.track_pageview({ page: pageType });

        setMixpanelTracked(true);
      } else if (initialNav) {
        Mixpanel.register({ active_page: initialNav, page_group: initialNav });
        Mixpanel.track_pageview({ page: initialNav });

        setMixpanelTracked(true);
      } else {
        // we don't know what page it is
        Mixpanel.register({ active_page: null, page_group: null });
      }
    }
  }, [initialNav, pageType, mixpanelTracked]);

  // Only an ERROR-intent dialog shows the raw-message details.
  const isErrorIntent = (displayedError?.intent ?? Intent.ERROR) === Intent.ERROR;
  // referenceId is set only for a backend requestId or a frontend FE- fingerprint (see
  // utils/error-reference.ts); without one there is nothing to report.
  const referenceId = displayedError?.referenceId;
  const showReportError = Boolean(referenceId) && !displayedError?.hideCancel;
  const messageText = typeof displayedError?.message === 'string' ? displayedError.message : '';
  const report = useErrorReport({
    referenceId: referenceId ?? '',
    errorMessage: displayedError?.rawMessage ?? messageText,
    error: displayedError
  });
  const noIdNote = referenceId
    ? undefined
    : NO_ID_SOURCE_NOTE[displayedError?.errorSource ?? 'none'];

  const errorModal = (
    <AlertDialog
      intent={displayedError?.intent ?? Intent.ERROR}
      show={Boolean(error)}
      onHide={displayedError?.onOkay ?? (() => {})}
      title={displayedError?.title ?? 'Error'}
      body={
        <>
          {formatMessage(displayedError?.message)}
          {noIdNote && <p>{noIdNote}</p>}
          {showReportError && report.body}
          {isErrorIntent && displayedError?.rawMessage && (
            <ErrorMoreInfo rawMessage={displayedError.rawMessage} code={displayedError.code} />
          )}
        </>
      }
      primaryAction={{ onClick: displayedError?.onOkay, design: Design.NEUTRAL }}
      additionalFooterContent={showReportError ? report.footer : undefined}
      passthroughProps={{ 'data-testid': 'error-dialog' }}
    />
  );

  const areYouSureModal = (
    <AlertDialog
      // Honor the caller-supplied intent (ModalAreYouSure inherits `intent?` from ModalError);
      // WARNING stays as the sensible default when a caller doesn't pick one, matching the
      // long-standing behavior when this field was hardcoded.
      intent={displayedAreYouSure?.intent ?? Intent.WARNING}
      show={Boolean(areYouSure)}
      onHide={displayedAreYouSure?.onCancel ?? (() => {})}
      title={displayedAreYouSure?.title ?? 'Are you sure?'}
      body={formatMessage(displayedAreYouSure?.message)}
      primaryAction={{ onClick: displayedAreYouSure?.onOkay, label: displayedAreYouSure?.okayText }}
      secondaryAction={{
        onClick: displayedAreYouSure?.onCancel,
        label: displayedAreYouSure?.cancelText
      }}
      passthroughProps={{
        'data-testid': 'are-you-sure-dialog',
        className: 'are-you-sure-dialog',
        backdropClassName: 'are-you-sure-backdrop'
      }}
    />
  );

  if (onlyModals) {
    // The one persistent, always-mounted instance of this branch (see main.tsx) is now the sole
    // renderer of `state.modals.error`/`areYouSureMore` for the whole app -- see the normal-path
    // return below, which deliberately no longer renders its own copy of these modals.
    return (
      <ErrorBoundary>
        <div>
          {errorModal}
          {areYouSureModal}
        </div>
      </ErrorBoundary>
    );
  }
  return (
    <div className={cn('bpm-page', className)} style={{ minHeight, minWidth }}>
      <ErrorBoundary>
        <NewVersionAlertBar />
        <div className='header'>
          <div className='title-container'>
            <Img
              className='bliss-point-logo'
              alt='Bliss Point'
              src='https://cdn.blisspointmedia.com/agencies/bpm/Tinuiti_Blisspoint_Logo_White.png'
              unloader={<span>Bliss Point</span>}
              fetchPriority='high'
              width={104}
              height={34}
            />
            <div className='divider' />
            {title && (
              <div className='title' data-testid='page-title'>
                {title}
              </div>
            )}
            {navs && (
              <Nav className='navs' variant='pills' activeKey={selectedNav} onSelect={ourOnNav}>
                {navs.map(({ label, key }) => (
                  <Nav.Item key={key}>
                    <Nav.Link bsPrefix='page-nav-item' eventKey={key}>
                      {navRenderer(label)}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            )}
          </div>
          {showHelpVideoTutorial && (
            <VideoTutorial showHelpButton={showHelpVideoTutorial} videoList={videoList} />
          )}
          <div className='actions'>{actions}</div>
        </div>
        {subHeader && <div className='page-sub-header'>{subHeader}</div>}
        <div className='page-toolbar-slot' ref={setToolbarSlot} />
        <PageToolbarSlotContext.Provider value={toolbarSlot}>
          <div className='body'>{children}</div>
        </PageToolbarSlotContext.Provider>
      </ErrorBoundary>
    </div>
  );
};
